import type { Optional, Split } from '@gov-nx/core/types';
import { hasOwnProperty, splitByDot } from '@gov-nx/core/types';
import { getServiceUrlByCode, ServiceCode } from '@gov-nx/module/service';
import { replaceVariablesInUrl } from '@gov-nx/utils/common/Request/query';
import { pageList } from '../../../module/page/src/definitions/pages';
import type { PagePath } from './helpertypes';

export const serviceRouteResolver = (
	service: keyof typeof ServiceCode
): string => getServiceUrlByCode(ServiceCode[service]) ?? '#';

type OtherChildren = Record<
	string,
	{
		locales: { cs: { slug: string } };
		children?: OtherChildren;
	}
>;

/**
 * Resolves a service subroute URL with support for variable path segments and query parameters.
 * Still needs some refinement but allows to generate service nested links with dynamic parameters in a consistent way.

 * Example:
 *   serviceSubRouteResolver('PPP', 'reklamace', { pppId: '123', foo: 'bar' }, ['pppId'])
 *   // => '/sluzba/ppp/reklamace/123?foo=bar'
 */
export const serviceSubRouteResolver = <SubRoutes>(
	service: keyof typeof ServiceCode,
	subRoute: SubRoutes,
	params?: Record<string, Optional<string | number>>,
	variableParts?: string[]
): string => {
	let baseUrl = [serviceRouteResolver(service), subRoute].join('/');
	const queryParams = params ? { ...params } : undefined;

	if (variableParts && params) {
		for (const part of variableParts) {
			const value = params[part];
			const encodedValue = value ? encodeURIComponent(String(value)) : '';
			if (value && !baseUrl.includes(encodedValue)) {
				baseUrl += `/${encodedValue}`;
				if (queryParams) {
					delete queryParams[part];
				}
			}
		}
	}
	return replaceVariablesInUrl(baseUrl, queryParams ?? null);
};

export const pageRouteResolver = (
	path: PagePath,
	params?: Record<string, Optional<string | number>>
): string => {
	const getPageUrlRecursive = (
		keys: Split<PagePath, '.'> | string[],
		obj: OtherChildren,
		slugs: string[]
	): string[] => {
		if (keys.length === 0) {
			return slugs;
		}
		const [name, ...remainingKeys] = keys;
		const currentObj = hasOwnProperty(obj, name) ? obj[name] : undefined;
		if (!currentObj) {
			return slugs;
		}

		const newSlugs = [...slugs, currentObj.locales.cs.slug];
		if (remainingKeys.length > 0 && currentObj.children) {
			return getPageUrlRecursive(remainingKeys, currentObj.children, newSlugs);
		}

		return newSlugs;
	};

	const chunks = getPageUrlRecursive(splitByDot(path), pageList.children, []);
	const link = `/${chunks.join('/')}`;

	return replaceVariablesInUrl(link, params ?? null);
};
